// combatpro/monster/monsterStudioEngine.js
//
// Combat PRO - Monster Studio Engine (Stage 55: Monster Studio Engine Foundation).
//
// The pure, framework-independent runtime/model layer for Monster Studio,
// migrated verbatim in behaviour from the legacy implementation in
// battletracker.html (the read-only master spec). This mirrors the role
// spellStudioEngine.js plays for the Spell Studio: data-in / data-out helpers
// only. Later Monster Studio stages (editor, library, persistence, preview,
// etc.) consume this module without modification.
//
// Every export is pure and deterministic for a given input, with one explicit
// exception: createMonsterId() draws on Date.now()/Math.random() to mint a fresh
// id (the framework-independent equivalent of the legacy viSId, and the direct
// analogue of spellStudioEngine.createSpellId). The deterministic id sequencer
// nextMonsterId() is provided for callers that prefer reproducible ids derived
// from an existing pool. All model factories accept their id as input so the
// transformations themselves remain deterministic.
//
// Scope migrated (pure helpers only): the new-monster factory, the official ->
// studio model conversion, the studio -> encounter selectable conversion, record
// normalisation, deep clone, card-array mapping, ability modifier, CSV parse /
// join, the display-name and save-status derivations, and the name-required
// validation. The legacy treats passive perception, XP, and proficiency as
// free-text fields (no calculation exists in the master spec), so no such
// calculators are fabricated here. Rendering, persistence, cloud, versions,
// collections, templates, search/filter, preview, and Test-In-Encounter are NOT
// in this module - they are runtime/UI concerns for later stages.
//
// No React, no JSX, no hooks, no context, no DOM, no querySelector, no innerHTML,
// no event handlers, no fetch, no localStorage, no navigation, no imports. No
// optional chaining. No em dash.

// ---- Model constants -------------------------------------------------------

// Default ability score block (battletracker.html 10872 / 10898). Callers should
// clone this rather than mutate the shared reference.
export const DEFAULT_ABILITY_SCORES = { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 };

// Size vocabulary (VI_SIZES, battletracker.html 10854).
export const STUDIO_SIZES = ['Tiny', 'Small', 'Medium', 'Large', 'Huge', 'Gargantuan'];

// Alignment vocabulary (VI_ALIGN, battletracker.html 10855). Editor select options.
export const STUDIO_ALIGNMENTS = ['Unaligned', 'Lawful Good', 'Neutral Good', 'Chaotic Good', 'Lawful Neutral', 'Neutral', 'Chaotic Neutral', 'Lawful Evil', 'Neutral Evil', 'Chaotic Evil'];

// Morale vocabulary (VI_MORALE, battletracker.html 10856). Editor select options.
export const STUDIO_MORALE = ['High', 'Steady', 'Shaken', 'Breaking'];

// Which model arrays are "card lists" (VI_CARD_LISTS, battletracker.html 10853).
// Maps a card-tab key to the model field that stores its { name, desc[, cost] }
// entries.
export const STUDIO_CARD_LISTS = {
  traits: 'traits',
  actions: 'actions',
  bonus: 'bonusActions',
  reactions: 'reactions',
  legendary: 'legendaryActions',
  lair: 'lairActions'
};

// ---- Identity helpers ------------------------------------------------------

// Framework-independent random id (legacy viSId, battletracker.html 10858).
// Not deterministic; use when no pool-derived id is required.
export function createMonsterId() {
  return 'm_' + Date.now().toString(36) + '_' + Math.floor(Math.random() * 1e6).toString(36);
}

// Deterministic sequential id (pure form of legacy viNextMonId, 10861-10866).
// Scans the provided list for the highest existing MON-###### id, takes the max
// of that and the provided counter, and returns the next id plus the updated
// counter. No globals are read or written; the caller owns the counter.
export function nextMonsterId(existingMonsters, counter) {
  var max = counter || 0;
  var list = Array.isArray(existingMonsters) ? existingMonsters : [];
  for (var i = 0; i < list.length; i++) {
    var m = list[i];
    var raw = (m && (m.customMonsterId || m.id)) || '';
    var match = /^MON-(\d+)$/.exec(raw);
    if (match) {
      var n = parseInt(match[1], 10);
      if (n > max) { max = n; }
    }
  }
  var next = max + 1;
  return { id: 'MON-' + ('000000' + next).slice(-6), counter: next };
}

// ---- Model factory + transforms --------------------------------------------

// New blank studio monster (legacy viNewStudioMonster, 10867-10890). The id is
// taken as input so the factory is deterministic; if omitted, a fresh random id
// is minted via createMonsterId. id and customMonsterId are set to the same
// value, matching the master spec.
export function createStudioMonster(id) {
  var cid = id || createMonsterId();
  return {
    id: cid, customMonsterId: cid, sourceMonster: null, customName: '', status: 'custom',
    versions: [], tags: [], collections: [],
    name: '', size: 'Medium', type: '', alignment: 'Unaligned', cr: '', xp: '', race: '', language: '',
    abilityScores: { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
    saves: '', skills: '', initiativeBonus: '', passivePerception: '',
    ac: '', hpRange: '', hitDice: '', resistances: '', immunities: '', vulnerabilities: '', conditionImmunities: '',
    speed: { walk: '30', climb: '', swim: '', fly: '', burrow: '', hover: false },
    traits: [], actions: [], bonusActions: [], reactions: [], legendaryActions: [], lairActions: [],
    spells: [],
    behaviour: { morale: 'Steady', leadership: '', retreatThreshold: '', combatNotes: '', aiNotes: '', tactics: '' },
    defaultConditions: '', specialEffects: '', notes: '',
    favourite: false, archived: false, lastSaved: null
  };
}

// Normalise a card array into { name, desc[, cost] } entries (legacy viMapCards,
// 10885-10890). Accepts plain strings or loosely shaped objects.
export function mapCards(arr) {
  if (!Array.isArray(arr)) { return []; }
  return arr.map(function(c) {
    if (typeof c === 'string') { return { name: c, desc: '' }; }
    return {
      name: (c && (c.name || c.title)) || '',
      desc: (c && (c.desc || c.description || c.text)) || '',
      cost: (c && c.cost) || undefined
    };
  });
}

// Convert an official MONSTERS_DB entry into an editable studio model (legacy
// viOfficialToStudio, 10892-10913). The new model's id is taken as input.
export function officialToStudioModel(official, id) {
  var o = official || {};
  var s = createStudioMonster(id);
  s.sourceMonster = o.name; s.customName = o.name; s.status = 'unmodified'; s.name = o.name;
  s.size = o.size || 'Medium'; s.type = o.race || o.type || ''; s.alignment = o.alignment || 'Unaligned';
  s.cr = (o.cr != null ? String(o.cr) : ''); s.xp = (o.xp != null ? String(o.xp) : '');
  s.race = o.race || ''; s.language = o.language || o.languages || '';
  s.abilityScores = Object.assign({ STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 }, (o.abilityScores || {}));
  s.saves = (typeof o.saves === 'string') ? o.saves : (o.saves ? Object.keys(o.saves).map(function(k) { return k + ' ' + o.saves[k]; }).join(', ') : '');
  s.skills = o.skills || ''; s.passivePerception = (o.passivePerception != null ? String(o.passivePerception) : '');
  s.ac = (o.ac != null ? String(o.ac) : ''); s.hpRange = o.hpRange || ''; s.hitDice = o.hitDice || '';
  s.resistances = o.resistances || ''; s.immunities = o.immunities || ''; s.vulnerabilities = o.vulnerabilities || ''; s.conditionImmunities = o.conditionImmunities || '';
  if (typeof o.speed === 'object' && o.speed) { s.speed = Object.assign(s.speed, o.speed); }
  else if (o.speed) { s.speed.walk = String(o.speed).replace(/[^0-9]/g, '') || s.speed.walk; }
  s.traits = mapCards(o.traits || o.abilities);
  s.actions = mapCards(o.actions || o.weapons);
  s.bonusActions = mapCards(o.bonusActions);
  s.reactions = mapCards(o.reactions);
  s.legendaryActions = mapCards(o.legendaryActions);
  s.lairActions = mapCards(o.lairActions);
  s.spells = Array.isArray(o.spells) ? o.spells.slice() : [];
  return s;
}

// Convert a studio model into the selection-safe object consumed by the existing
// encounter selectMonster() flow (legacy viStudioToSelectable, 10915-10929).
export function studioToSelectable(s) {
  var model = s || {};
  return {
    name: model.customName || model.name || 'Custom Monster',
    size: model.size, race: model.race || model.type, class: '', language: model.language,
    ac: model.ac, passivePerception: model.passivePerception, spellDC: 0,
    hpRange: model.hpRange, cr: model.cr, saves: {},
    immunities: model.immunities || '', resistances: model.resistances || '',
    abilityScores: model.abilityScores || {},
    abilities: [].concat(model.traits || [], model.actions || [], model.bonusActions || [], model.reactions || []).map(function(c) {
      return { name: c.name || '', desc: c.desc || '', custom: true };
    }),
    weapons: [],
    legendaryActions: (model.legendaryActions || []).map(function(c) {
      return { name: c.name || '', desc: c.desc || '', cost: c.cost || 1 };
    }),
    spells: (model.spells || []).slice(),
    __custom: true, __studioId: model.customMonsterId || model.id
  };
}

// Coerce a loaded / imported record into a full studio model (legacy
// viStudioNormalize, 11436-11444). fallbackId supplies an id when the record has
// none, keeping the function deterministic (the legacy fell back to viNextMonId).
export function normalizeStudioMonster(record, fallbackId) {
  var m = Object.assign(createStudioMonster(fallbackId), record || {});
  if (!m.customMonsterId) { m.customMonsterId = m.id || fallbackId; }
  m.id = m.customMonsterId;
  if (!Array.isArray(m.versions)) { m.versions = []; }
  if (!Array.isArray(m.tags)) { m.tags = []; }
  if (!Array.isArray(m.collections)) { m.collections = []; }
  return m;
}

// Deep clone via JSON round-trip (legacy viClone, 10358). Returns the original
// reference if the value is not JSON-serialisable, matching the master spec.
export function cloneStudioMonster(value) {
  try { return JSON.parse(JSON.stringify(value)); }
  catch (e) { return value; }
}

// ---- Derived / calculated helpers ------------------------------------------

// Ability modifier as a signed string, e.g. "+2" / "-1" (legacy viStudioAbilMod,
// 11306). Non-numeric input is treated as 10 (modifier "+0").
export function abilityModifier(score) {
  var n = parseInt(score, 10);
  if (isNaN(n)) { n = 10; }
  var m = Math.floor((n - 10) / 2);
  return (m >= 0 ? '+' : '') + m;
}

// Display name fallback used across the studio (the customName || name ||
// 'Custom Monster' pattern, e.g. battletracker.html 10917).
export function studioDisplayName(model) {
  var m = model || {};
  return m.customName || m.name || 'Custom Monster';
}

// Save status derivation (legacy viStudioSave, 11246): a model derived from an
// official source saves as 'modified'; an original saves as 'custom'.
export function deriveSaveStatus(model) {
  var m = model || {};
  return m.sourceMonster ? 'modified' : 'custom';
}

// ---- CSV helpers -----------------------------------------------------------

// Parse a comma-separated string into a trimmed, non-empty array (the pure core
// of legacy viStudioSetCsv, 11445-11447). Used for tags / collections input.
export function parseCsv(value) {
  return String(value || '').split(',').map(function(s) { return s.trim(); }).filter(function(s) { return s.length; });
}

// Join an array into a "a, b, c" string (the inverse, matching the join(', ')
// pattern the master spec uses to render tag / collection / save lists).
export function toCsv(list) {
  if (!Array.isArray(list)) { return ''; }
  return list.filter(function(s) { return s != null && String(s).length; }).join(', ');
}

// ---- Validation (no UI) ----------------------------------------------------

// Name-required validation, the pure equivalent of the guard in legacy
// viStudioSave (11242): a monster must have a customName or a name. Returns a
// { valid, error } result; the caller owns any UI response (alert / tab switch),
// which is not performed here.
export function validateStudioMonster(model) {
  var m = model || {};
  if (!m.customName && !m.name) {
    return { valid: false, error: 'Give the monster a name first (Identity tab).' };
  }
  return { valid: true, error: '' };
}
